import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { getUuid } from '@sibe/utilities/common';
import {
  HsGraphicPoints,
  HsPointsMaterial,
  HsRegularGeometryData,
  HsShaderData,
  HsShaderObjectType,
  hsMakeGeometry,
  type HsDefaultShader,
  type HsHypersceneViewer,
  type HsRegularGeometry,
} from '@sibe/hyperscene';
import { DemoViewerPage } from '../../classes/demo-viewer-page.class';

/** Signed distance to a surface: negative inside. */
type SignedDistance = (x: number, y: number, z: number) => number;

const TORUS_SIDE: number = 100;
const POINT_SIZE: number = 4;

/** A twisted double torus. */
const TORUS_SDF: SignedDistance = (x: number, y: number, z: number): number => {
  const radius: number = Math.sqrt(x * x + z * z) - 0.7;
  const angle: number = Math.atan2(x, z);
  const yRotated: number = y * Math.cos(angle * 2.5) - radius * Math.sin(angle * 2.5);
  const radiusRotated: number = y * Math.sin(angle * 2.5) + radius * Math.cos(angle * 2.5);
  return Math.sqrt(radiusRotated ** 2 + (Math.abs(yRotated) - 0.2) ** 2) - 0.1;
};

/**
 * Samples a `side`^3 grid over -1..1 and keeps the points inside the surface, colored by their grid position
 * (red, green and blue follow x, y and z).
 */
function makePointCloudData(side: number, distance: SignedDistance): HsRegularGeometryData {
  const positions: number[] = [];
  const colors: number[] = [];
  for (let i: number = 0; i < side; i++) {
    for (let j: number = 0; j < side; j++) {
      for (let k: number = 0; k < side; k++) {
        const x: number = (i / side) * 2 - 1;
        const y: number = (j / side) * 2 - 1;
        const z: number = (k / side) * 2 - 1;
        if (distance(x, y, z) < 0) {
          positions.push(x, y, z);
          colors.push(i / side, j / side, k / side);
        }
      }
    }
  }
  // Point geometry keeps the per-point colors where meshes keep normals.
  return new HsRegularGeometryData(positions, colors);
}

/** A point cloud sampled from a signed distance function. */
@Component({
  selector: 'hyperscene-points',
  templateUrl: './points.component.html',
  styleUrls: ['./points.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class PointsDemo extends DemoViewerPage {
  protected initScene(viewer: HsHypersceneViewer): void {
    const geometry: HsRegularGeometry | null = hsMakeGeometry(
      viewer.surface.graphics,
      makePointCloudData(TORUS_SIDE, TORUS_SDF)
    );
    if (geometry === null) {
      return;
    }
    const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    shaderData.addColor().addDepth().addPoints();
    const material: HsPointsMaterial<HsDefaultShader> = new HsPointsMaterial(viewer.gl, shaderData.makeShader());
    const points: HsGraphicPoints = new HsGraphicPoints(getUuid(), 'Points', geometry, material);
    points.setPointsSize(viewer.gl, POINT_SIZE);
    // A third of a turn around (1, 1, 1), as the legacy point cloud factory oriented its clouds.
    points.worldMatrix.compose([0, 4, 0], [0.5, 0.5, 0.5, 0.5], [10, 10, 10]);
    points.updateBoundingSphere();
    viewer.add(points);

    viewer.camera.fov = 1;
    viewer.camera.worldMatrix.setPosition([2, 10, 30]);
    viewer.camera.updateProjectionMatrix();
  }
}
